import RefreshIcon from '@mui/icons-material/Refresh';
import type {
  ExposureCommodity,
  ExposureCustomerSegment,
  ExposurePolicy,
  ExposureRegistryEntry,
  ExposureSupplier,
} from '@akem/shared';
import {
  Alert,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import React from 'react';

import { useExposureEntry } from '../hooks/useExposureEntry.js';

type ExposureEntryState = ReturnType<typeof useExposureEntry>;

interface ExposureSnapshotProps {
  instrumentId: string | null;
  title?: string;
  dense?: boolean;
  sx?: SxProps<Theme>;
  state?: ExposureEntryState;
}

type RevenueShareInfo = {
  geo?: string | null;
  pct?: number | null;
};

const formatPercent = (value?: number | null): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return `${Math.round(value * 100)}%`;
};

const summarizeSuppliers = (suppliers?: ExposureSupplier[]): string[] => {
  if (!Array.isArray(suppliers)) return [];
  return suppliers
    .filter((supplier): supplier is ExposureSupplier => Boolean(supplier?.name))
    .slice(0, 3)
    .map((supplier) => {
      const name = supplier.name.trim();
      const share =
        typeof supplier.share_of_input_pct === 'number'
          ? `${Math.round(supplier.share_of_input_pct * 100)}%`
          : null;
      const descriptor =
        typeof supplier.component === 'string' && supplier.component.trim().length > 0
          ? supplier.component.trim()
          : null;
      const parts = [descriptor, share].filter(Boolean);
      return parts.length ? `${name} • ${parts.join(' · ')}` : name;
    });
};

const summarizePolicies = (policies?: ExposurePolicy[]): string[] => {
  if (!Array.isArray(policies)) return [];
  return policies
    .filter((policy): policy is ExposurePolicy => Boolean(policy?.policy))
    .slice(0, 3)
    .map((policy) => {
      const strength =
        typeof policy.exposure_strength === 'number'
          ? `${Math.round(policy.exposure_strength * 100)}%`
          : null;
      const scopes = Array.isArray(policy.scope)
        ? policy.scope.filter((s): s is string => typeof s === 'string' && s.trim().length > 0)
        : [];
      const scopeText = scopes.length ? ` — ${scopes.join(', ')}` : '';
      return strength ? `${policy.policy} (${strength})${scopeText}` : `${policy.policy}${scopeText}`;
    });
};

const summarizeCustomers = (segments?: ExposureCustomerSegment[]): string[] => {
  if (!Array.isArray(segments)) return [];
  return segments
    .filter((segment): segment is ExposureCustomerSegment => Boolean(segment?.segment))
    .slice(0, 3)
    .map((segment) => {
      const share =
        typeof segment.share_of_rev_pct === 'number'
          ? `${Math.round(segment.share_of_rev_pct * 100)}%`
          : undefined;
      const sensitivity =
        typeof segment.growth_sensitivity === 'number'
          ? segment.growth_sensitivity
          : undefined;
      if (share != null && sensitivity != null) {
        return `${segment.segment} • ${share} (β ${sensitivity >= 0 ? '+' : ''}${sensitivity})`;
      }
      if (share != null) return `${segment.segment} • ${share}`;
      if (sensitivity != null) return `${segment.segment} • β ${sensitivity >= 0 ? '+' : ''}${sensitivity}`;
      return segment.segment;
    });
};

const summarizeCommodities = (commodities?: ExposureCommodity[]): string[] => {
  if (!Array.isArray(commodities)) return [];
  return commodities
    .filter((commodity): commodity is ExposureCommodity => Boolean(commodity?.commodity))
    .slice(0, 3)
    .map((commodity) => {
      const share =
        typeof commodity.cost_share_pct === 'number'
          ? `${Math.round(commodity.cost_share_pct * 100)}%`
          : undefined;
      const beta =
        typeof commodity.price_beta_to_margin === 'number'
          ? commodity.price_beta_to_margin
          : undefined;
      if (share != null && beta != null) {
        return `${commodity.commodity} • ${share} (β ${beta >= 0 ? '+' : ''}${beta})`;
      }
      if (share != null) return `${commodity.commodity} • ${share}`;
      if (beta != null) return `${commodity.commodity} • β ${beta >= 0 ? '+' : ''}${beta}`;
      return commodity.commodity;
    });
};

const summarizeGeos = (shares?: RevenueShareInfo[] | null): string[] => {
  if (!Array.isArray(shares) || shares.length === 0) return [];
  return shares
    .filter(
      (share): share is RevenueShareInfo & { geo: string } =>
        typeof share?.geo === 'string' && share.geo.trim().length > 0
    )
    .map((share) => ({
      geo: share.geo!.trim(),
      pct: typeof share.pct === 'number' ? share.pct : null,
    }))
    .sort((a, b) => (b.pct ?? 0) - (a.pct ?? 0))
    .slice(0, 4)
    .map((share) => `${share.geo} ${formatPercent(share.pct ?? null)}`);
};

const formatUpdatedAt = (entry?: ExposureRegistryEntry | null): string | null => {
  const updatedAtRaw =
    (entry as { updatedAt?: string | null })?.updatedAt ??
    (entry as { createdAt?: string | null })?.createdAt ??
    null;
  if (typeof updatedAtRaw !== 'string' || updatedAtRaw.trim().length === 0) {
    return null;
  }
  const parsed = new Date(updatedAtRaw);
  if (Number.isNaN(parsed.getTime())) return updatedAtRaw;
  return parsed.toLocaleString();
};

export const ExposureSnapshot: React.FC<ExposureSnapshotProps> = ({
  instrumentId,
  title = 'Exposure snapshot',
  dense = false,
  sx,
  state,
}) => {
  const fallbackState = useExposureEntry(state ? null : instrumentId);
  const { exposure, loading, error, empty, refresh } = state ?? fallbackState;

  const geos = React.useMemo(() => summarizeGeos(exposure?.geography?.revenue_by_geo), [exposure]);
  const suppliers = React.useMemo(() => summarizeSuppliers(exposure?.suppliers), [exposure]);
  const customers = React.useMemo(
    () => summarizeCustomers(exposure?.customer_segments),
    [exposure]
  );
  const policies = React.useMemo(() => summarizePolicies(exposure?.policy), [exposure]);
  const commodities = React.useMemo(
    () => summarizeCommodities(exposure?.commodities),
    [exposure]
  );

  const updatedAtText = formatUpdatedAt(exposure);

  return (
    <Stack
      spacing={dense ? 0.75 : 1}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: dense ? 0.75 : 1,
        bgcolor: 'background.paper',
        ...sx,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Typography variant={dense ? 'subtitle2' : 'subtitle1'} sx={{ flexGrow: 1 }}>
          {title}
        </Typography>
        <Tooltip title="Refresh exposure coverage">
          <span>
            <IconButton
              size="small"
              onClick={() => refresh()}
              disabled={!instrumentId || loading}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {updatedAtText ? (
          <Typography variant="caption" color="text.secondary">
            Updated {updatedAtText}
          </Typography>
        ) : null}
      </Stack>

      {!instrumentId ? (
        <Alert severity="info" variant="outlined">
          Provide a profile or ticker to view stewarded exposures.
        </Alert>
      ) : loading && !exposure ? (
        <Stack alignItems="center" spacing={0.75} sx={{ py: dense ? 1 : 1.5 }}>
          <CircularProgress size={18} />
          <Typography variant="caption" color="text.secondary">
            Loading exposure coverage…
          </Typography>
        </Stack>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : empty || !exposure ? (
        <Alert severity="info" variant="outlined">
          No steward exposure coverage has been published yet.
        </Alert>
      ) : (
        <Stack spacing={dense ? 0.5 : 0.75}>
          {geos.length > 0 ? (
            <Stack spacing={0.25}>
              <Typography variant="caption" color="text.secondary">
                Revenue by geography
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {geos.map((geo) => (
                  <Chip key={geo} size="small" variant="outlined" label={geo} />
                ))}
              </Stack>
            </Stack>
          ) : null}

          {suppliers.length > 0 ? (
            <Stack spacing={0.25}>
              <Typography variant="caption" color="text.secondary">
                Top suppliers
              </Typography>
              <Stack spacing={0.25}>
                {suppliers.map((supplier) => (
                  <Typography key={supplier} variant="body2">
                    {supplier}
                  </Typography>
                ))}
              </Stack>
            </Stack>
          ) : null}

          {customers.length > 0 ? (
            <Stack spacing={0.25}>
              <Typography variant="caption" color="text.secondary">
                Customer segments
              </Typography>
              <Stack spacing={0.25}>
                {customers.map((segment) => (
                  <Typography key={segment} variant="body2">
                    {segment}
                  </Typography>
                ))}
              </Stack>
            </Stack>
          ) : null}

          {commodities.length > 0 ? (
            <Stack spacing={0.25}>
              <Typography variant="caption" color="text.secondary">
                Commodities
              </Typography>
              <Stack spacing={0.25}>
                {commodities.map((commodity) => (
                  <Typography key={commodity} variant="body2">
                    {commodity}
                  </Typography>
                ))}
              </Stack>
            </Stack>
          ) : null}

          {policies.length > 0 ? (
            <Stack spacing={0.25}>
              <Typography variant="caption" color="text.secondary">
                Policy & regulatory
              </Typography>
              <Stack spacing={0.25}>
                {policies.map((policy) => (
                  <Typography key={policy} variant="body2">
                    {policy}
                  </Typography>
                ))}
              </Stack>
            </Stack>
          ) : null}

          {geos.length === 0 &&
          suppliers.length === 0 &&
          customers.length === 0 &&
          commodities.length === 0 &&
          policies.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Exposure record exists but no detailed breakdown is available yet.
            </Typography>
          ) : null}
        </Stack>
      )}
    </Stack>
  );
};
